import React from 'react';
import type { AnchorHTMLAttributes, ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { color, display, fontSize, lineHeight, themeGet } from 'styled-system';
import type { DisplayProps, FontSizeProps, LineHeightProps } from 'styled-system';

import type { Color } from '../theme/colors/types';
import { getThemeColor } from '../theme/helper';
import type { TextDecorationProps } from '../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../tracking/dispatchTrackingEventGA4';
import type { ThemedAnchorButtonWithHTMLAttrs } from './Button';
import { Button } from './Button';

interface LinkTrackingProps {
  eventName: string;
  text?: string;
  clickedText?: string;
  placement?: string;
  [key: string]: string;
}

interface LinkProps
  extends
    FontSizeProps,
    LineHeightProps,
    TextDecorationProps,
    DisplayProps,
    AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  tracking?: LinkTrackingProps;
  styled?: boolean;
  color?: Color;
  ref?: React.Ref<HTMLAnchorElement>;
}

export type LinkStylesProps = Pick<LinkProps, 'styled' | 'color'> & TextDecorationProps;

export const linkStyles = (props: LinkStylesProps) => {
  const textDecoration = props.textDecoration || 'none';
  const colorToken = props.color || 'sys.neutral.text.default';

  return (
    props.styled &&
    css`
      color: ${getThemeColor(colorToken)(props)};
      text-decoration: ${textDecoration};
      text-underline-position: from-font;
      text-decoration-thickness: from-font;
      cursor: pointer;

      &:hover {
        color: ${getThemeColor('sys.neutral.text.hover')(props)};

        text-decoration: underline;
        text-underline-position: from-font;
        text-decoration-thickness: from-font;
        text-decoration-color: ${getThemeColor('sys.neutral.text.hover')(props)};
      }

      &:active {
        color: ${getThemeColor('sys.neutral.text.active')(props)};

        text-decoration: underline;
        text-underline-position: from-font;
        text-decoration-thickness: from-font;
        text-decoration-color: ${getThemeColor('sys.neutral.text.active')(props)};
      }

      &:focus-visible {
        border-radius: ${themeGet('radii.medium')(props)};
        outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')(props)};
        outline-offset: 2px;
      }
    `
  );
};

const UnstyledSecondaryLinkButton: ThemedAnchorButtonWithHTMLAttrs = styled(Button)<any>`
  /* Shop-Reboot :'( */
  text-decoration: none !important;

  ${lineHeight};
  ${fontSize};
`;

const SecondaryLinkButton = (props: ComponentProps<typeof UnstyledSecondaryLinkButton>) => (
  <UnstyledSecondaryLinkButton as="a" kind="secondary" {...props} />
);

export { SecondaryLinkButton };

const StyledLink = styled.a<any>`
  color: inherit;
  text-decoration: none;

  ${linkStyles};

  ${color};
  ${fontSize};
  ${lineHeight};
  ${display};

  &:hover {
    color: ${themeGet('colors.sys.neutral.text.hover')};
    border-color: ${themeGet('colors.sys.neutral.text.hover')};
  }
`;

export const Link = ({
  tracking,
  onClick,
  styled: styledProp = true,
  ref,
  ...restProps
}: LinkProps) => {
  const handleClick = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(evt);
    if (tracking) {
      const { eventName, text, clickedText, placement, ...rest } = tracking;

      dispatchTrackingEventGA4({
        event: eventName,
        element: 'link',
        ...(text && { link_text: text }),
        ...(clickedText && { clicked_text: clickedText }),
        ...(placement && { placement }),
        ...rest,
      });
    }
  };

  return <StyledLink {...restProps} styled={styledProp} ref={ref} onClick={handleClick} />;
};

Link.displayName = 'Link';

export const LinkWithIcon = styled(Link)<any>`
  &:hover svg,
  &:focus svg {
    fill: ${themeGet('colors.sys.primary.background.default')};
  }
  &:active svg {
    fill: ${themeGet('colors.sys.primary.background.active')};
  }
`;
